import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { SiteLayout } from '../components/SiteLayout';
import {
  type BlogDraft,
  type ComplianceReview,
  type TopicIdea,
  useApproveArticle,
  useDraftIdea,
  useGenerateBlogDraft,
  useGenerateImage,
  useRejectArticle,
  useSuggestTopics,
  useTopicFeedback,
} from '../api/marketing';
import { colors } from '../theme';

const MotionBox = motion.create(Box);

// Local-only comment shape for Phase 1 (no backend persistence yet — this is
// the interactive reading experience; comments/likes harden to real storage in
// a later phase).
interface LocalComment {
  id: number;
  name: string;
  body: string;
  at: string;
}

/**
 * Marketing intelligence → Blog ("Toast & Light").
 *
 * Lives at /marketing/blog. Generates a Toast & Light-style article via the
 * blog endpoint and renders it as a real, interactive reading experience —
 * like + comments (local for now) and an HY product sidebar. Phase 1: the
 * experience. Phase 2 (later): persist articles + comments and go truly public.
 */
export function MarketingBlogPage() {
  const generate = useGenerateBlogDraft();
  const suggest = useSuggestTopics();
  const draftIdea = useDraftIdea();
  const heroImage = useGenerateImage();
  const approve = useApproveArticle();
  const reject = useRejectArticle();
  const [topic, setTopic] = useState('');
  const [draft, setDraft] = useState<BlogDraft | null>(null);
  const [heroUrl, setHeroUrl] = useState<string | null>(null);

  // Review stage: the article's DB id (queue-drafted articles only), its
  // compliance verdict, and its live status (updates on approve/reject).
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [compliance, setCompliance] = useState<ComplianceReview | null>(null);
  const [reviewStatus, setReviewStatus] = useState<string | null>(null);

  // Two views on one page: the topic queue ('list') and a full-page article
  // ('article'). Writing a topic opens the article; 'Back to topics' returns.
  const [view, setView] = useState<'list' | 'article'>('list');

  // Suggested-topic queue + which one is currently being drafted.
  const [ideas, setIdeas] = useState<TopicIdea[]>([]);
  const [draftingId, setDraftingId] = useState<number | null>(null);

  // Interactive state (local).
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState<LocalComment[]>([]);
  const [commentName, setCommentName] = useState('');
  const [commentBody, setCommentBody] = useState('');

  // A fresh article resets engagement, seeds a believable like count, opens the
  // full-page article view (scrolled to top), and kicks off the hero image.
  const showDraft = (result: BlogDraft) => {
    setDraft(result);
    setLiked(false);
    setLikeCount(Math.floor(40 + (result.title?.length ?? 0)));
    setComments([]);
    setHeroUrl(null);
    setCompliance(result.compliance ?? null);
    setReviewStatus(result.status ?? null);
    setView('article');
    window.scrollTo({ top: 0, behavior: 'auto' });

    // Generate a real hero image from the article's featured-image concept, the
    // way a blog leads with a photo. Whiskey/cigar articles anchor to a real HY
    // product so the bottle matches the brand; lifestyle skips the anchor.
    const concept = result.featured_image_concept?.trim();
    if (concept) {
      const category = inferCategory(result);
      heroImage
        .mutateAsync({
          visual_direction: concept,
          aspect_ratio: '16:9',
          use_brand_reference: category !== 'lifestyle',
          category,
          // Composite the real HY bottle for whiskey heroes (no duplicate/garbled
          // bottle). Backend falls back to two-pass for cigar/lifestyle.
          composite: true,
        })
        .then((r) => setHeroUrl(r.url))
        .catch(() => setHeroUrl(null));
    }
  };

  const backToTopics = () => {
    setView('list');
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleGenerate = async () => {
    // Free-topic generation isn't part of the review queue — no reviewable id.
    setReviewId(null);
    showDraft(await generate.mutateAsync({ topic: topic.trim() || undefined }));
  };

  const handleSuggest = async () => {
    const result = await suggest.mutateAsync({});
    setIdeas(result.topics);
  };

  const handleWriteIdea = async (idea: TopicIdea) => {
    // Persisted ideas have an id and draft through the queue (compliance runs
    // server-side); ones without (no DB) fall back to drafting from the title.
    setDraftingId(idea.id);
    setReviewId(idea.id);
    try {
      const result = idea.id
        ? await draftIdea.mutateAsync(idea.id)
        : await generate.mutateAsync({ topic: idea.title });
      showDraft(result);
    } finally {
      setDraftingId(null);
    }
  };

  const handleApprove = async () => {
    if (reviewId == null) return;
    const r = await approve.mutateAsync(reviewId);
    setReviewStatus(r.status);
  };

  const handleReject = async () => {
    if (reviewId == null) return;
    const r = await reject.mutateAsync(reviewId);
    setReviewStatus(r.status);
  };

  const addComment = () => {
    if (!commentBody.trim()) return;
    setComments((prev) => [
      {
        id: Date.now(),
        name: commentName.trim() || 'Guest',
        body: commentBody.trim(),
        at: new Date().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
      },
      ...prev,
    ]);
    setCommentBody('');
  };

  return (
    <SiteLayout subtitle="Marketing Intelligence" showDesktopHint>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        {view === 'list' ? (
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <Box sx={{ mb: 2 }}>
              <RouterLink
                to="/marketing"
                style={{
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
                Back to Marketing
              </RouterLink>
            </Box>

            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: colors.gold,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              Toast &amp; Light
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: 28, md: 38 },
                fontWeight: 700,
                color: colors.textPrimary,
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              The HY blog
            </Typography>
            <Typography sx={{ fontSize: 14, color: colors.textMuted, mb: 3, maxWidth: 720 }}>
              Suggest topics from this week&apos;s signals and pick one to write, or generate a
              Toast &amp; Light article from your own topic. Then preview it as readers would see
              it.
            </Typography>

            {/* Suggest-topics panel */}
            <Box sx={{ mb: 3, maxWidth: 720 }}>
              <Button
                variant="outlined"
                startIcon={
                  suggest.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <LightbulbOutlinedIcon />
                  )
                }
                onClick={handleSuggest}
                disabled={suggest.isPending}
                sx={{
                  borderColor: colors.gold,
                  color: colors.goldDark,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { borderColor: colors.goldDark, bgcolor: `${colors.gold}11` },
                }}
              >
                {suggest.isPending ? 'Finding topics…' : 'Suggest topics'}
              </Button>

              {suggest.isError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {(suggest.error as Error).message}
                </Alert>
              ) : null}

              {suggest.data?.error ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Couldn&apos;t suggest topics ({suggest.data.error}). Try again.
                </Alert>
              ) : null}

              {suggest.isPending ? (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12.5, color: colors.textMuted, mb: 1.5 }}>
                    Reading this week&apos;s news, trends, and what&apos;s engaging. This takes
                    about half a minute.
                  </Typography>
                  <Stack spacing={1.5}>
                    {[0, 1, 2].map((i) => (
                      <IdeaCardSkeleton key={i} />
                    ))}
                  </Stack>
                </Box>
              ) : ideas.length ? (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {ideas.map((idea, i) => (
                    <IdeaCard
                      key={idea.id ?? idea.topic_key ?? idea.title}
                      idea={idea}
                      rank={i + 1}
                      drafting={draftingId !== null && draftingId === idea.id}
                      busy={draftIdea.isPending || generate.isPending}
                      onWrite={() => handleWriteIdea(idea)}
                    />
                  ))}
                </Stack>
              ) : null}
            </Box>

            {/* Generate panel */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 4, maxWidth: 720 }}
            >
              <TextField
                placeholder="Optional topic, e.g. 'a fall whiskey and cigar pairing'"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 13,
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: colors.gold },
                    '&.Mui-focused fieldset': { borderColor: colors.gold },
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={
                  generate.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                onClick={handleGenerate}
                disabled={generate.isPending}
                sx={{
                  bgcolor: colors.gold,
                  '&:hover': { bgcolor: colors.goldDark },
                  textTransform: 'none',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  px: 2.5,
                }}
              >
                {generate.isPending ? 'Writing…' : 'Generate article'}
              </Button>
            </Stack>

            {generate.isError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {(generate.error as Error).message}
              </Alert>
            ) : null}

            {generate.isPending ? (
              <Stack spacing={2} sx={{ maxWidth: 760 }}>
                <Skeleton variant="text" width="70%" height={48} />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
              </Stack>
            ) : null}
          </MotionBox>
        ) : null}

        {view === 'article' && draft && !draft.error ? (
          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={backToTopics}
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600,
                  px: 0,
                  '&:hover': { bgcolor: 'transparent', color: colors.goldDark },
                }}
              >
                Back to topics
              </Button>
            </Box>

            {/* ── Review bar (queue-drafted articles only) ── */}
            {reviewId != null ? (
              <ReviewBar
                compliance={compliance}
                status={reviewStatus}
                busy={approve.isPending || reject.isPending}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ) : null}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 300px' },
                gap: 4,
                alignItems: 'start',
              }}
            >
              {/* ── Article ── */}
              <Box component="article">
                {/* Hero image — leads the post like a real blog. Generating →
                    skeleton; ready → the image; failed → the concept text. */}
                {heroImage.isPending ? (
                  <Box sx={{ mb: 3, position: 'relative' }}>
                    <Skeleton
                      variant="rectangular"
                      sx={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 2 }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        color: colors.textMuted,
                      }}
                    >
                      Creating the hero image…
                    </Typography>
                  </Box>
                ) : heroUrl ? (
                  <Box
                    component="img"
                    src={heroUrl}
                    alt={draft.title}
                    sx={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      objectFit: 'cover',
                      borderRadius: 2,
                      mb: 3,
                    }}
                  />
                ) : draft.featured_image_concept ? (
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: '#fefcf6',
                      border: `1px dashed ${colors.gold}66`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: colors.gold,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        mb: 0.5,
                      }}
                    >
                      Featured image
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13.5, color: colors.textSecondary, lineHeight: 1.5 }}
                    >
                      {draft.featured_image_concept}
                    </Typography>
                  </Box>
                ) : null}

                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontSize: { xs: 28, md: 40 },
                    fontWeight: 700,
                    color: colors.textPrimary,
                    lineHeight: 1.15,
                    mb: 1.5,
                  }}
                >
                  {draft.title}
                </Typography>
                {draft.excerpt ? (
                  <Typography
                    sx={{ fontSize: 17, color: colors.textSecondary, fontStyle: 'italic', mb: 2.5 }}
                  >
                    {draft.excerpt}
                  </Typography>
                ) : null}

                {/* Body — render markdown lightly (headings + paragraphs) */}
                <Box sx={{ '& p': { mb: 2 } }}>{renderMarkdown(draft.body_markdown)}</Box>

                {draft.tags?.length ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75, mt: 3 }}>
                    {draft.tags.map((t) => (
                      <Chip
                        key={t}
                        label={`#${t}`}
                        size="small"
                        sx={{ bgcolor: '#f3f4f6', color: colors.textSecondary, fontSize: 12 }}
                      />
                    ))}
                  </Stack>
                ) : null}

                <Divider sx={{ my: 3, borderColor: colors.borderLight }} />

                {/* ── Like + comment bar ── */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Button
                    startIcon={
                      liked ? <FavoriteIcon sx={{ color: colors.error }} /> : <FavoriteBorderIcon />
                    }
                    onClick={() => {
                      setLiked((v) => !v);
                      setLikeCount((c) => c + (liked ? -1 : 1));
                    }}
                    sx={{
                      textTransform: 'none',
                      color: liked ? colors.error : colors.textSecondary,
                      fontWeight: 600,
                    }}
                  >
                    {likeCount} {likeCount === 1 ? 'like' : 'likes'}
                  </Button>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ color: colors.textMuted }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: 13.5 }}>{comments.length}</Typography>
                  </Stack>
                </Stack>

                {/* Add comment */}
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
                    Leave a comment
                  </Typography>
                  <TextField
                    placeholder="Your name"
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    size="small"
                    sx={{ maxWidth: 240 }}
                  />
                  <TextField
                    placeholder="Share your thoughts…"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    size="small"
                    multiline
                    minRows={2}
                  />
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={addComment}
                      disabled={!commentBody.trim()}
                      sx={{
                        borderColor: colors.gold,
                        color: colors.goldDark,
                        textTransform: 'none',
                        fontWeight: 700,
                      }}
                    >
                      Post comment
                    </Button>
                  </Box>
                </Stack>

                {/* Comment list */}
                <Stack spacing={2}>
                  {comments.map((c) => (
                    <Stack key={c.id} direction="row" spacing={1.5}>
                      <Avatar sx={{ bgcolor: colors.gold, width: 34, height: 34, fontSize: 14 }}>
                        {c.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{c.name}</Typography>
                          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                            {c.at}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 13.5, color: colors.textSecondary, mt: 0.25 }}>
                          {c.body}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* ── HY product sidebar (rotating 3-at-a-time) ── */}
              <ProductShelf />
            </Box>
          </MotionBox>
        ) : view === 'article' && draft?.error ? (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={backToTopics}
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600,
                  px: 0,
                  '&:hover': { bgcolor: 'transparent', color: colors.goldDark },
                }}
              >
                Back to topics
              </Button>
            </Box>
            <Alert severity="info">
              Couldn&apos;t generate a draft ({draft.error}). Try again.
            </Alert>
          </Box>
        ) : null}
      </Container>
    </SiteLayout>
  );
}

// Source → short label + tint for the badge on each idea card.
const SOURCE_META: Record<string, { label: string; color: string }> = {
  news: { label: 'Timely', color: '#1d4ed8' },
  pioneer: { label: 'White space', color: '#7c3aed' },
  pattern: { label: 'Proven', color: '#047857' },
  evergreen: { label: 'Evergreen', color: '#92400e' },
};

interface IdeaCardProps {
  idea: TopicIdea;
  rank: number;
  drafting: boolean;
  busy: boolean;
  onWrite: () => void;
}

/** One proposed topic in the suggestion queue, with a "Write this" action. */
/** Loading placeholder shaped like a real IdeaCard so the wait previews the
 *  actual result (short cards) instead of large blank bars. */
function IdeaCardSkeleton() {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.surfaceSubtle,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <Skeleton variant="circular" width={22} height={22} sx={{ flexShrink: 0, mt: '2px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="70%" sx={{ fontSize: 15 }} />
        <Skeleton variant="text" width="95%" sx={{ fontSize: 13 }} />
        <Skeleton variant="text" width="45%" sx={{ fontSize: 12 }} />
      </Box>
      <Skeleton variant="rounded" width={92} height={30} sx={{ flexShrink: 0, borderRadius: 1 }} />
    </Box>
  );
}

// Quick reasons offered on a thumbs-down, so the feedback tells the engine WHY.
function IdeaCard({ idea, rank, drafting, busy, onWrite }: IdeaCardProps) {
  const meta = (idea.source && SOURCE_META[idea.source]) || null;
  const feedback = useTopicFeedback();
  const [vote, setVote] = useState<'up' | 'down' | null>(null);
  const [askReason, setAskReason] = useState(false);
  const [reasonText, setReasonText] = useState('');

  const sendVote = (v: 'up' | 'down', reason?: string) => {
    setVote(v);
    setAskReason(v === 'down' && reason === undefined);
    if (idea.id != null) {
      feedback.mutate({ id: idea.id, vote: v, reason: reason ?? undefined });
    }
  };

  const submitReason = () => {
    const r = reasonText.trim() || undefined;
    setAskReason(false);
    if (idea.id != null) {
      feedback.mutate({ id: idea.id, vote: 'down', reason: r });
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.surfaceSubtle,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 18,
          color: colors.gold,
          fontWeight: 700,
          mt: '2px',
        }}
      >
        {rank}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: colors.textPrimary }}>
            {idea.title}
          </Typography>
          {meta ? (
            <Chip
              label={meta.label}
              size="small"
              sx={{
                height: 18,
                fontSize: 10.5,
                fontWeight: 700,
                color: meta.color,
                bgcolor: `${meta.color}14`,
              }}
            />
          ) : null}
          {typeof idea.score === 'number' ? (
            <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
              {Math.round(idea.score)}
            </Typography>
          ) : null}
        </Stack>
        {idea.angle ? (
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 0.25 }}>
            {idea.angle}
          </Typography>
        ) : null}
        {idea.why_now ? (
          <Typography sx={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>
            Why now: {idea.why_now}
          </Typography>
        ) : null}

        {/* Thumbs up/down — steers future suggestions. */}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
          <IconButton
            size="small"
            aria-label="Good suggestion"
            onClick={() => sendVote('up')}
            sx={{ color: vote === 'up' ? '#047857' : colors.textMuted, p: 0.5 }}
          >
            {vote === 'up' ? (
              <ThumbUpAltIcon sx={{ fontSize: 16 }} />
            ) : (
              <ThumbUpOffAltIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
          <IconButton
            size="small"
            aria-label="Not a good suggestion"
            onClick={() => sendVote('down')}
            sx={{ color: vote === 'down' ? '#b91c1c' : colors.textMuted, p: 0.5 }}
          >
            {vote === 'down' ? (
              <ThumbDownAltIcon sx={{ fontSize: 16 }} />
            ) : (
              <ThumbDownOffAltIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
          {vote && !askReason ? (
            <Typography sx={{ fontSize: 11, color: colors.textMuted, ml: 0.5 }}>
              Thanks, noted
            </Typography>
          ) : null}
        </Stack>

        {askReason ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
            <TextField
              size="small"
              placeholder="Tell us why — optional"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitReason();
              }}
              autoFocus
              inputProps={{ maxLength: 120 }}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': { fontSize: 12, py: 0.75 },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#fca5a5' },
                  '&:hover fieldset': { borderColor: '#f87171' },
                  '&.Mui-focused fieldset': { borderColor: '#b91c1c' },
                },
              }}
            />
            <Button
              size="small"
              onClick={submitReason}
              sx={{
                fontSize: 11,
                px: 1.5,
                py: 0.5,
                minWidth: 0,
                bgcolor: '#fef2f2',
                color: '#b91c1c',
                '&:hover': { bgcolor: '#fee2e2' },
              }}
            >
              Submit
            </Button>
          </Stack>
        ) : null}
      </Box>
      <Button
        size="small"
        variant="contained"
        startIcon={drafting ? <CircularProgress size={14} color="inherit" /> : <EditNoteIcon />}
        onClick={onWrite}
        disabled={busy}
        sx={{
          bgcolor: colors.gold,
          '&:hover': { bgcolor: colors.goldDark },
          textTransform: 'none',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {drafting ? 'Writing…' : 'Write this'}
      </Button>
    </Box>
  );
}

// Verdict → label + color for the review bar.
type VerdictMeta = { label: string; color: string; bg: string };
const VERDICT_UNKNOWN: VerdictMeta = {
  label: 'Review unavailable',
  color: '#6b7280',
  bg: '#f9fafb',
};
const VERDICT_META: Record<string, VerdictMeta> = {
  compliant: { label: 'Compliant', color: '#047857', bg: '#ecfdf5' },
  revise: { label: 'Needs revision', color: '#b45309', bg: '#fffbeb' },
  block: { label: 'Blocked, auto-rejected', color: '#b91c1c', bg: '#fef2f2' },
  error: VERDICT_UNKNOWN,
};

interface ReviewBarProps {
  compliance: ComplianceReview | null;
  status: string | null;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}

/** Compliance verdict + human approve/reject for a queue-drafted article. */
function ReviewBar({ compliance, status, busy, onApprove, onReject }: ReviewBarProps) {
  const verdict = compliance?.verdict ?? 'error';
  const meta = VERDICT_META[verdict] ?? VERDICT_UNKNOWN;
  const decided = status === 'approved' || status === 'rejected';
  // A hard block was auto-rejected server-side; approving is not offered.
  const canApprove = !decided && verdict !== 'block';

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${meta.color}44`,
        bgcolor: meta.bg,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              label={meta.label}
              size="small"
              sx={{ bgcolor: meta.color, color: '#fff', fontWeight: 700, fontSize: 11 }}
            />
            {decided ? (
              <Chip
                label={status === 'approved' ? 'Approved' : 'Rejected'}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: 11, borderColor: meta.color, color: meta.color }}
              />
            ) : null}
          </Stack>
          {compliance?.summary ? (
            <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.75 }}>
              {compliance.summary}
            </Typography>
          ) : null}
          {compliance?.issues?.length ? (
            <Stack spacing={0.25} sx={{ mt: 0.75 }}>
              {compliance.issues.slice(0, 4).map((iss, i) => (
                <Typography key={i} sx={{ fontSize: 12, color: colors.textMuted }}>
                  <b>{iss.severity}</b>
                  {iss.quoted ? `: “${iss.quoted}”` : ''}
                  {iss.rule ? ` — ${iss.rule}` : ''}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Box>

        {!decided ? (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              disabled={!canApprove || busy}
              onClick={onApprove}
              sx={{
                bgcolor: '#047857',
                '&:hover': { bgcolor: '#065f46' },
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={onReject}
              sx={{
                borderColor: '#b91c1c',
                color: '#b91c1c',
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Reject
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

/** Best-effort category for a blog draft, used to anchor the hero image to the
 *  right HY product. The backend draft carries no category, so we infer it from
 *  the title/tags; HY is whiskey-first, so that's the default. */
function inferCategory(draft: BlogDraft): 'whiskey' | 'cigar' | 'lifestyle' {
  const hay = `${draft.title} ${draft.tags?.join(' ') ?? ''}`.toLowerCase();
  if (/\bcigar|cigars|smoke|wrapper|humidor\b/.test(hay)) return 'cigar';
  if (/\bwhiskey|whisky|bourbon|rye|barrel|distill|pour|glass\b/.test(hay)) return 'whiskey';
  if (/\bhost|entertain|gather|holiday|pairing|ritual|lifestyle\b/.test(hay)) return 'lifestyle';
  return 'whiskey';
}

// How many products show at once, and how often the shelf advances.
const SHELF_WINDOW = 3;
const SHELF_INTERVAL_MS = 5000;

/**
 * The "From the HY shelf" sidebar. Shows SHELF_WINDOW products at a time and
 * advances by a full page every SHELF_INTERVAL_MS, looping through all of
 * HY_PRODUCTS. Pauses while the reader hovers so a product doesn't swap away
 * mid-click.
 */
function ProductShelf() {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const pageCount = Math.ceil(HY_PRODUCTS.length / SHELF_WINDOW);

  useEffect(() => {
    if (paused || pageCount <= 1) return;
    const id = setInterval(() => setPage((p) => (p + 1) % pageCount), SHELF_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, pageCount]);

  // The window of products for the current page, wrapping so the last page
  // backfills from the start (e.g. 7,8,1) rather than showing a short page.
  const start = page * SHELF_WINDOW;
  const shown = Array.from({ length: Math.min(SHELF_WINDOW, HY_PRODUCTS.length) }, (_, i) => {
    return HY_PRODUCTS[(start + i) % HY_PRODUCTS.length];
  }).filter((p): p is (typeof HY_PRODUCTS)[number] => p !== undefined);

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        position: { md: 'sticky' },
        top: { md: 24 },
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.surfaceSubtle,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: colors.gold,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          mb: 1.5,
        }}
      >
        From the HY shelf
      </Typography>
      <Stack spacing={2}>
        {shown.map((p) => (
          <MotionBox
            key={p.name}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: '#fff',
              border: `1px solid ${colors.border}`,
            }}
          >
            <Box
              component="img"
              src={p.image}
              alt={p.name}
              loading="lazy"
              sx={{
                width: '100%',
                height: 140,
                objectFit: 'contain',
                borderRadius: 1,
                mb: 1,
                bgcolor: '#1a1410',
                p: 1,
              }}
            />
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: colors.textPrimary }}>
              {p.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 1 }}>
              {p.tagline}
            </Typography>
            <Button
              component="a"
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              fullWidth
              variant="contained"
              sx={{
                bgcolor: colors.gold,
                '&:hover': { bgcolor: colors.goldDark },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              Shop
            </Button>
          </MotionBox>
        ))}
      </Stack>

      {/* Page dots — show which trio is on screen + let the reader jump. */}
      {pageCount > 1 ? (
        <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ mt: 2 }}>
          {Array.from({ length: pageCount }, (_, i) => (
            <Box
              key={i}
              onClick={() => setPage(i)}
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: i === page ? colors.gold : colors.border,
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

// HY shelf for the sidebar — real product photos + per-product store links.
// Images live in public/products/ (pulled from the Shopify CDN).
const HY_PRODUCTS = [
  {
    name: 'HY Bottled-in-Bond Bourbon',
    tagline: 'Bottled in Bond · 100 proof',
    image: '/products/bottled-in-bond-bourbon.png',
    url: 'https://store.hootenyoung.com/products/bottled-in-bond-bourbon',
  },
  {
    name: 'HY × Jack Carr Warrior Proof 16yr',
    tagline: 'Special Reserve · American whiskey',
    image: '/products/warrior-proof-16yr.png',
    url: 'https://store.hootenyoung.com/products/signed-edition-hooten-young-x-jack-carr-warrior-proof-american-whiskey-limited-bottling-copy',
  },
  {
    name: 'HY American Whiskey 12yr',
    tagline: 'Aged 12 years · 46% ABV',
    image: '/products/american-whiskey-12yr.png',
    url: 'https://store.hootenyoung.com/products/hooten-young-american-whiskey',
  },
  {
    name: 'HY Rye Whiskey 7yr',
    tagline: 'Operation Gothic Serpent · double gold',
    image: '/products/rye-whiskey.png',
    url: 'https://store.hootenyoung.com/products/hooten-young-rye-whiskey',
  },
  {
    name: 'HY × Jack Carr Cigar',
    tagline: 'Hand-rolled · The Terminal List partnership',
    image: '/products/jack-carr-cigar.png',
    url: 'https://store.hootenyoung.com/products/pre-sale-hooten-young-jack-carr',
  },
  {
    name: 'HY Right To Bear Arms Cigar',
    tagline: 'NRA edition · box of 20',
    image: '/products/rtba-cigar.png',
    url: 'https://store.hootenyoung.com/products/rtba-box-20',
  },
  {
    name: 'HY Ma Deuce Cigar',
    tagline: 'Torpedo · 93-rated, Cigar & Spirits',
    image: '/products/ma-deuce-cigar.png',
    url: 'https://store.hootenyoung.com/products/ma-deuce',
  },
  {
    name: 'HY Operator Cigar',
    tagline: '#10 Cigar of the Year 2023',
    image: '/products/operator-cigar.png',
    url: 'https://store.hootenyoung.com/products/operator',
  },
];

/** Minimal markdown renderer — headings (##/###) + paragraphs. Avoids pulling
 *  a markdown lib for Phase 1; the body is short structured prose. */
function renderMarkdown(md: string): React.ReactNode {
  if (!md) return null;
  const blocks = md.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith('### ')) {
      return (
        <Typography
          key={i}
          sx={{ fontSize: 17, fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}
        >
          {trimmed.slice(4)}
        </Typography>
      );
    }
    if (trimmed.startsWith('## ')) {
      return (
        <Typography
          key={i}
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: 22,
            fontWeight: 700,
            color: colors.textPrimary,
            mt: 3,
            mb: 1,
          }}
        >
          {trimmed.slice(3)}
        </Typography>
      );
    }
    return (
      <Typography key={i} sx={{ fontSize: 15.5, color: colors.textSecondary, lineHeight: 1.7 }}>
        {trimmed}
      </Typography>
    );
  });
}
